import {
  useEffect,
  useLayoutEffect,
  useRef,
  EffectCallback,
  useCallback,
} from 'react';
import { useLocation } from 'react-router-dom';

export const useLockBodyScroll = () => {
  useLayoutEffect(() => {
    // Get original body overflow
    const originalStyle = window.getComputedStyle(document.body).overflow;
    // Prevent scrolling on mount
    document.body.style.overflow = 'hidden';
    // Re-enable scrolling when component unmounts
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []); // Empty array ensures effect is only run on mount and unmount
};

export const useInterval = (callback: () => void, delay: number | null) => {
  const savedCallback = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    // Don't schedule if no delay is specified.
    // Note: 0 is a valid value for delay.
    if (!delay && delay !== 0) {
      return () => {};
    }

    function tick() {
      savedCallback.current?.();
    }
    savedCallback.current?.();
    const id = setInterval(tick, delay);
    return () => clearInterval(id);
  }, [delay]);
};

// eslint-disable-next-line react-hooks/exhaustive-deps
export const useEffectOnce = (effect: EffectCallback) => useEffect(effect, []);

export const useQuery = () => new URLSearchParams(useLocation().search);

interface DebounceOptions {
  leading?: boolean;
  trailing?: boolean;
}

export function useDebouncedCallback<T extends (...args: unknown[]) => void>(
  callback: T,
  delay: number,
  options?: DebounceOptions
): (...args: Parameters<T>) => void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastArgsRef = useRef<Parameters<T> | null>(null);
  const invokedRef = useRef(false);

  const debounced = useCallback(
    (...args: Parameters<T>) => {
      const callNow = options?.leading && !timeoutRef.current;
      lastArgsRef.current = args;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        if (options?.trailing !== false && lastArgsRef.current) {
          callback(...lastArgsRef.current);
          lastArgsRef.current = null;
        }
        timeoutRef.current = null;
        invokedRef.current = false;
      }, delay);

      if (callNow && !invokedRef.current) {
        callback(...args);
        invokedRef.current = true;
      }
    },
    [callback, delay, options]
  );

  return debounced;
}
